"use client";

var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Check, ChevronDown, ShieldAlert, ShieldCheck, X } from "lucide-react";
import { useState, useCallback } from "react";
import { getProviderIcon } from "@llmgateway/shared/components";
import { Badge } from "./ui/badge.js";
import { Button } from "./ui/button.js";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "./ui/command.js";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover.js";
import { Switch } from "./ui/switch.js";
export function MultiProviderSelector(_a) {
  var providers = _a.providers,
    selectedProviders = _a.selectedProviders,
    onProvidersChange = _a.onProvidersChange,
    _b = _a.placeholder,
    placeholder = _b === void 0 ? "Select providers..." : _b;
  var _c = useState(false),
    open = _c[0],
    setOpen = _c[1];
  var _d = useState(false),
    compatibleOnly = _d[0],
    setCompatibleOnly = _d[1];
  var isPolicyAware = providers.some(function (provider) {
    return provider.meetsPolicy !== undefined;
  });
  var visibleProviders = isPolicyAware && compatibleOnly ? providers.filter(function (provider) {
    return provider.meetsPolicy !== false || selectedProviders.includes(provider.id);
  }) : providers;
  var handleProviderToggle = useCallback(function (providerId) {
    var isSelected = selectedProviders.includes(providerId);
    if (isSelected) {
      onProvidersChange(selectedProviders.filter(function (id) {
        return id !== providerId;
      }));
    } else {
      onProvidersChange(__spreadArray(__spreadArray([], selectedProviders, true), [providerId], false));
    }
  }, [selectedProviders, onProvidersChange]);
  var removeProvider = useCallback(function (providerId) {
    onProvidersChange(selectedProviders.filter(function (id) {
      return id !== providerId;
    }));
  }, [selectedProviders, onProvidersChange]);
  return _jsxs("div", {
    className: "space-y-2",
    children: [selectedProviders.length > 0 && _jsx("div", {
      className: "flex flex-wrap gap-1",
      children: selectedProviders.map(function (providerId) {
        var _a_0, _b;
        var provider = providers.find(function (p) {
          return p.id === providerId;
        });
        var ProviderIcon = getProviderIcon(providerId);
        return _jsxs(Badge, {
          variant: "secondary",
          className: "flex items-center gap-1",
          children: [ProviderIcon && _jsx(ProviderIcon, {
            className: "h-3 w-3"
          }), (_a_0 = provider === null || provider === void 0 ? void 0 : provider.name) !== null && _a_0 !== void 0 ? _a_0 : providerId, (provider === null || provider === void 0 ? void 0 : provider.meetsPolicy) === false && _jsx("span", {
            title: ((_b = provider.policyNotes) === null || _b === void 0 ? void 0 : _b.length) ? provider.policyNotes.join("; ") : "Does not meet policy requirements",
            children: _jsx(ShieldAlert, {
              className: "h-3 w-3 text-red-500",
              "aria-label": "Does not meet policy requirements"
            })
          }), _jsx(Button, {
            variant: "ghost",
            size: "sm",
            className: "h-3 w-3 p-0 hover:bg-transparent",
            onClick: function () {
              return removeProvider(providerId);
            },
            children: _jsx(X, {
              className: "h-2 w-2"
            })
          })]
        }, providerId);
      })
    }), _jsxs(Popover, {
      open: open,
      onOpenChange: setOpen,
      children: [_jsx(PopoverTrigger, {
        asChild: true,
        children: _jsxs(Button, {
          variant: "outline",
          role: "combobox",
          "aria-expanded": open,
          className: "w-full justify-between",
          children: [_jsx("span", {
            className: "text-left truncate",
            children: selectedProviders.length === 0 ? placeholder : "".concat(selectedProviders.length, " provider").concat(selectedProviders.length === 1 ? "" : "s", " selected")
          }), _jsx(ChevronDown, {
            className: "ml-2 h-4 w-4 shrink-0 opacity-50"
          })]
        })
      }), _jsx(PopoverContent, {
        className: "w-80 p-0 max-h-[400px]",
        align: "start",
        children: _jsxs(Command, {
          className: "flex flex-col",
          children: [_jsx(CommandInput, {
            placeholder: "Search providers..."
          }), isPolicyAware && _jsxs("div", {
            className: "flex items-center justify-between gap-2 border-b px-3 py-2",
            children: [_jsx("span", {
              className: "text-xs text-muted-foreground",
              children: "Only providers that meet policy requirements"
            }), _jsx(Switch, {
              checked: compatibleOnly,
              onCheckedChange: setCompatibleOnly,
              "aria-label": "Only show providers that meet policy requirements"
            })]
          }), _jsx("div", {
            className: "max-h-[300px] overflow-y-auto",
            children: _jsxs(CommandList, {
              children: [_jsx(CommandEmpty, {
                children: isPolicyAware && compatibleOnly ? "No providers meet the policy requirements." : "No providers found."
              }), visibleProviders.map(function (provider_0) {
                var isSelected = selectedProviders.includes(provider_0.id);
                var ProviderIcon_0 = getProviderIcon(provider_0.id);
                return _jsxs(CommandItem, {
                  value: "".concat(provider_0.id, " ").concat(provider_0.name),
                  onSelect: function () {
                    return handleProviderToggle(provider_0.id);
                  },
                  className: "flex items-center justify-between py-3 cursor-pointer",
                  children: [_jsxs("div", {
                    className: "flex min-w-0 items-center gap-2",
                    children: [ProviderIcon_0 && _jsx(ProviderIcon_0, {
                      className: "h-4 w-4 shrink-0"
                    }), _jsxs("div", {
                      className: "min-w-0",
                      children: [_jsx("span", {
                        className: "font-medium",
                        children: provider_0.name
                      }), provider_0.meetsPolicy === false && provider_0.policyNotes && provider_0.policyNotes.length > 0 && _jsx("p", {
                        className: "truncate text-xs text-red-600 dark:text-red-400",
                        title: provider_0.policyNotes.join("; "),
                        children: provider_0.policyNotes.join(" · ")
                      })]
                    })]
                  }), _jsxs("div", {
                    className: "flex shrink-0 items-center gap-2",
                    children: [provider_0.meetsPolicy !== undefined ? provider_0.meetsPolicy ? _jsx(ShieldCheck, {
                      className: "h-4 w-4 text-emerald-600",
                      "aria-label": "Meets policy requirements"
                    }) : _jsx(ShieldAlert, {
                      className: "h-4 w-4 text-red-500",
                      "aria-label": "Does not meet policy requirements"
                    }) : provider_0.color && _jsx("div", {
                      className: "w-3 h-3 rounded-full",
                      style: {
                        backgroundColor: provider_0.color
                      }
                    }), isSelected && _jsx(Check, {
                      className: "h-4 w-4 text-green-600"
                    })]
                  })]
                }, provider_0.id);
              })]
            })
          }), isPolicyAware && _jsxs("div", {
            className: "flex items-center gap-3 border-t px-3 py-2 text-xs text-muted-foreground",
            children: [_jsxs("span", {
              className: "flex items-center gap-1",
              children: [_jsx(ShieldCheck, {
                className: "h-3.5 w-3.5 text-emerald-600"
              }), "Meets policy requirements"]
            }), _jsxs("span", {
              className: "flex items-center gap-1",
              children: [_jsx(ShieldAlert, {
                className: "h-3.5 w-3.5 text-red-500"
              }), "Does not meet"]
            })]
          })]
        })
      })]
    })]
  });
}